// GoogleReviewPopup - Pop-up de demande d'avis Google après clôture de mission
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, ExternalLink, Heart, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

const GOOGLE_REVIEW_URL = "https://g.page/r/CSdDnxYKhP4PEBM/review";

const GoogleReviewPopup = ({ isOpen, onClose, userName }) => {
  const [animateStars, setAnimateStars] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      // Déclencher l'animation des étoiles après ouverture
      const timer = setTimeout(() => setAnimateStars(true), 500);
      return () => clearTimeout(timer);
    }
    setAnimateStars(false);
  }, [isOpen]);
  
  const handleReviewClick = () => {
    // Ouvrir dans un nouvel onglet
    window.open(GOOGLE_REVIEW_URL, '_blank', 'noopener,noreferrer');
    // Marquer comme vu et fermer après un délai
    setTimeout(onClose, 1000);
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="glass-card max-w-md w-full p-8 border-2 border-success/50 relative overflow-hidden"
          >
            {/* Close button */}
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Confetti / Sparkles background effect */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    background: ['#00FF94', '#00F5FF', '#FFD700', '#FF00FF'][i % 4],
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={animateStars ? {
                    opacity: [0, 1, 0],
                    scale: [0, 1.5, 0],
                    y: [0, -30, -60],
                  } : {}}
                  transition={{
                    duration: 2,
                    delay: i * 0.1,
                    repeat: Infinity,
                    repeatDelay: 3,
                  }}
                />
              ))}
            </div>
            
            {/* Content */}
            <div className="text-center relative z-10">
              {/* Success Icon */}
              <motion.div 
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-success/20 flex items-center justify-center"
                animate={{ 
                  scale: [1, 1.1, 1],
                  boxShadow: [
                    '0 0 0 0 rgba(0, 255, 148, 0.4)',
                    '0 0 0 20px rgba(0, 255, 148, 0)',
                    '0 0 0 0 rgba(0, 255, 148, 0)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="w-10 h-10 text-success" />
              </motion.div>
              
              {/* Title */}
              <h2 className="font-orbitron text-2xl font-bold text-white mb-2">
                Mission <span className="text-success">Accomplie</span> !
              </h2>
              
              <p className="text-gray-400 mb-6">
                {userName ? `Félicitations ${userName}, votre` : 'Votre'} dossier a été clôturé avec succès.
              </p>
              
              {/* Stars animation */}
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0.3, scale: 0.8 }}
                    animate={animateStars ? { 
                      opacity: 1, 
                      scale: 1,
                      rotate: [0, 10, -10, 0]
                    } : {}}
                    transition={{ delay: i * 0.15, duration: 0.5 }}
                  >
                    <Star 
                      className="w-8 h-8 text-yellow-400" 
                      fill="#FACC15"
                      style={{ filter: 'drop-shadow(0 0 8px rgba(250, 204, 21, 0.6))' }}
                    />
                  </motion.div>
                ))}
              </div>
              
              {/* Message */}
              <div className="glass-card p-4 mb-6 border border-cyan-400/30 bg-cyan-400/5">
                <p className="text-gray-300 text-sm leading-relaxed">
                  <Heart className="w-4 h-4 text-[#FF3366] inline mr-1" />
                  Aidez d'autres <span className="text-cyan-400 font-bold">Toulousains</span> à s'effacer en nous laissant un avis !
                </p>
              </div>
              
              {/* CTA Button */}
              <Button
                onClick={handleReviewClick}
                className="w-full py-6 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-orbitron font-bold text-lg hover:from-yellow-400 hover:to-amber-400 transition-all"
                data-testid="google-review-btn"
              >
                <Star className="w-5 h-5 mr-2" fill="currentColor" />
                Laisser un avis Google
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
              
              {/* Skip link */}
              <button 
                onClick={onClose}
                className="mt-4 text-gray-500 hover:text-gray-400 text-sm transition-colors"
              >
                Peut-être plus tard
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { GoogleReviewPopup, GOOGLE_REVIEW_URL };
export default GoogleReviewPopup;
